import { useEffect, useState } from 'react';
import { VaultGate } from '@/components/VaultGate';
import { api } from '@/lib/api';

// MIRRORS THE WIRE FORMAT, not the server's internal source type.
//
// GET /api/options/:underlying renames fields on the way out
// (terminalRoutes.ts): expiration -> expiry, side -> type,
// implied_vol -> iv, open_interest -> openInterest. This interface
// declared the SOURCE names, so every one of those was undefined at
// runtime while TypeScript reported no error -- strikes rendered with
// blank bid/ask/greeks, and the honest "No contracts" message was
// suppressed because the array was non-empty.
interface Contract {
  symbol:        string;
  underlying?:   string;
  expiry:        string;
  strike:        number;
  type:          'call' | 'put';
  bid:           number | null;
  ask:           number | null;
  last:          number | null;
  volume:        number | null;
  openInterest:  number | null;
  iv:            number | null;
  ivRank?:       number | null;
  delta:         number | null;
  gamma:         number | null;
  theta:         number | null;
  vega:          number | null;
  rho:           number | null;
}

// Null-safe formatters. Every cell below went through `.toFixed()` on a
// value that could be undefined; with no ErrorBoundary that took the whole
// app down rather than blanking one cell.
const fx = (v: number | null | undefined, dp: number): string =>
  typeof v === 'number' && Number.isFinite(v) ? v.toFixed(dp) : '';
const pct = (v: number | null | undefined): string =>
  typeof v === 'number' && Number.isFinite(v) ? (v * 100).toFixed(1) + '%' : '';

/**
 * OptionsChain — renders a paired call/put strike ladder with greeks.
 * Uses the server's /api/options/:underlying endpoint (which calls
 * Alpaca's options snapshot API).
 *
 * Note: this route uses the *server's* admin Alpaca subscription, not
 * the vault key. We hit /api/options for chain data because chain data
 * is broker-independent (any Alpaca subscriber sees the same chain).
 * The user's vault key is used for *trading* (place option order via
 * brokerCall), not for fetching market data.
 */
export function OptionsChain() {
  const [underlying,  setUnderlying] = useState('AAPL');
  const [symbolInput, setSymbolInput] = useState('AAPL');
  const [expiration,  setExpiration] = useState<string>('');
  const [contracts,   setContracts] = useState<Contract[]>([]);
  const [loading,     setLoading] = useState(false);
  const [error,       setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Drop the previous underlying/expiry's chain before loading the new one,
    // so a slow or failed load cannot leave another instrument's strikes and
    // Greeks rendered under this symbol's header.
    setContracts([]);
    setLoading(true); setError(null);
    const qs = new URLSearchParams();
    if (expiration) qs.set('expiration', expiration);
    // Two bugs lived in this call:
    //   1. raw fetch() sent NO Authorization header, so the endpoint —
    //      which has always required a Passport JWT — returned 401 and the
    //      401 body was then read as the chain.
    //   2. it read `d.contracts`, but the handler that actually serves
    //      this path returns a FLAT ARRAY. `undefined` -> [] -> the page
    //      showed "No contracts" for every symbol, which reads as a data
    //      availability problem rather than a wiring bug.
    // api() attaches auth and throws on non-2xx so failures surface.
    api<Contract[]>(`/api/options/${encodeURIComponent(underlying)}${qs.toString() ? `?${qs}` : ''}`)
      .then(d => {
        if (cancelled) return;
        setContracts(Array.isArray(d) ? d : []);
        setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError((e as Error).message || String(e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [underlying, expiration]);

  // Group by strike for the ladder layout (call on left, put on right).
  const strikes = Array.from(new Set(contracts.map(c => c.strike))).sort((a, b) => a - b);
  const byStrike = new Map<number, { call?: Contract; put?: Contract }>();
  for (const c of contracts) {
    const row = byStrike.get(c.strike) ?? {};
    // `c.side` was the source-type name; the wire field is `type`. This is
    // the line that silently bucketed nothing, leaving every strike empty.
    if (c.type === 'call' || c.type === 'put') row[c.type] = c;
    byStrike.set(c.strike, row);
  }

  return (
    <div style={{ padding: '20px 24px', color: '#fafaf9', fontFamily: 'Inter, sans-serif' }}>
      <h2 style={{ margin: '0 0 14px', fontWeight: 500 }}>Options chain</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <input
          value={symbolInput}
          onChange={e => setSymbolInput(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === 'Enter') setUnderlying(symbolInput); }}
          placeholder="Underlying (AAPL)"
          style={{ background: '#292524', border: '1px solid rgba(245,244,238,0.10)', color: '#fafaf9', padding: '6px 10px', borderRadius: 4, fontSize: 13 }}
        />
        <input
          type="date"
          value={expiration}
          onChange={e => setExpiration(e.target.value)}
          style={{ background: '#292524', border: '1px solid rgba(245,244,238,0.10)', color: '#fafaf9', padding: '6px 10px', borderRadius: 4, fontSize: 13 }}
        />
        <button onClick={() => setUnderlying(symbolInput)} style={{ padding: '6px 14px', background: '#d4a45a', color: '#0c0a09', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>Load</button>
        {loading && <span style={{ color: '#a8a29e', fontSize: 12 }}>loading...</span>}
        {error   && <span style={{ color: '#fca5a5', fontSize: 12 }}>{error.slice(0, 80)}</span>}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'JetBrains Mono, monospace' }}>
          <thead>
            <tr style={{ background: '#292524', textAlign: 'right' }}>
              <th colSpan={6} style={{ color: '#4ade80', padding: '8px 6px' }}>CALLS</th>
              <th style={{ background: '#292524', color: '#d4a45a', padding: '8px 6px' }}>STRIKE</th>
              <th colSpan={6} style={{ color: '#e8b5a8', padding: '8px 6px' }}>PUTS</th>
            </tr>
            <tr style={{ background: '#1c1917', color: '#a8a29e', fontWeight: 400 }}>
              {['Delta', 'Theta', 'IV', 'Vol', 'Bid', 'Ask'].map(h => <th key={'c'+h} style={{ textAlign: 'right', padding: '5px 6px' }}>{h}</th>)}
              <th style={{ textAlign: 'center', padding: '5px 6px' }}></th>
              {['Bid', 'Ask', 'Vol', 'IV', 'Theta', 'Delta'].map(h => <th key={'p'+h} style={{ textAlign: 'right', padding: '5px 6px' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {strikes.map(strike => {
              const row = byStrike.get(strike)!;
              const c = row.call; const p = row.put;
              return (
                <tr key={strike} style={{ borderBottom: '1px solid rgba(245,244,238,0.06)' }}>
                  <td style={{ textAlign: 'right', padding: '4px 6px' }}>{fx(c?.delta, 3)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 6px' }}>{fx(c?.theta, 3)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 6px' }}>{pct(c?.iv)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 6px' }}>{c?.volume ?? ''}</td>
                  <td style={{ textAlign: 'right', padding: '4px 6px' }}>{fx(c?.bid, 2)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 6px' }}>{fx(c?.ask, 2)}</td>
                  <td style={{ textAlign: 'center', padding: '4px 6px', color: '#d4a45a', fontWeight: 500, background: 'rgba(212,164,90,0.05)' }}>{strike.toFixed(2)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 6px' }}>{fx(p?.bid, 2)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 6px' }}>{fx(p?.ask, 2)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 6px' }}>{p?.volume ?? ''}</td>
                  <td style={{ textAlign: 'right', padding: '4px 6px' }}>{pct(p?.iv)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 6px' }}>{fx(p?.theta, 3)}</td>
                  <td style={{ textAlign: 'right', padding: '4px 6px' }}>{fx(p?.delta, 3)}</td>
                </tr>
              );
            })}
            {strikes.length === 0 && !loading && (
              <tr><td colSpan={13} style={{ padding: 24, textAlign: 'center', color: '#a8a29e' }}>No contracts. Try another expiration or underlying.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function OptionsChainPage() {
  return (
    <VaultGate silent>
      <OptionsChain />
    </VaultGate>
  );
}
